import React, { useCallback, useRef, useState } from 'react';
import { Skeleton, message } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { PER_PAGE_OTHER } from '../../../constants/common';
import { getTileWidgetListRepository } from '../../../repository/mytm/shop/shopRepository';
import { SHOP_TYPE } from '../../../types/shop';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { MainstaysPageHeader } from '../../organisms/mainstays/MainstaysPageHeader';
import { MainstaysTable } from '../../organisms/mainstays/MainstaysTable';
import { FormMainstaysModal } from '../../organisms/mainstays/FormMainstaysModal';
import SelectButtons from '../../atoms/select-buttons/SelectButtons';
import { useCountrySelection } from '../../../hooks/useCountrySelection';

export const Mainstays = () => {
  const [pageActive, setPageActive] = useState(1);
  const [loading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [record, setDataRecord] = useState<any>();
  const errorCount = useRef(0);
  const [countrySelected, setCountrySelected] = useCountrySelection(null);

  const { data, isValidating, error, mutate } = useSWR(
    countrySelected ? [pageActive, 1, countrySelected] : null,
    (pageNumber: number) =>
      getTileWidgetListRepository({
        page: pageNumber,
        take: PER_PAGE_OTHER,
        country: countrySelected!,
        type: SHOP_TYPE.MAINSTAYS,
        sort: true,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const handleChangePageActive = useCallback(value => {
    setPageActive(value);
    window.scrollTo(0, 0);
  }, []);

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  const recordMainstays = data?.widgets?.map((item: any, index: number) => ({ ...item, ...{ index } }));

  return (
    <Container>
      <MainstaysPageHeader setIsOpenModal={setIsOpenModal} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      {!data && (
        <Container>
          <Skeleton active />
        </Container>
      )}
      {data && (
        <MainstaysTable
          handleChangePageActive={handleChangePageActive}
          onRefresh={mutate}
          records={recordMainstays || []}
          loading={loading || isValidating}
          setIsOpenModal={setIsOpenModal}
          setDataRecord={setDataRecord}
          pagination={{
            onChange: handleChangePageActive,
            pageSize: PER_PAGE_OTHER,
            current: pageActive,
            total: data?.total,
          }}
        />
      )}

      {isOpenModal && countrySelected && (
        <FormMainstaysModal
          setDataRecord={setDataRecord}
          record={record}
          onRefresh={mutate}
          isOpenModal={isOpenModal}
          onClose={onCloseModal}
          countrySelected={countrySelected}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }

  & .ant-page-header {
    padding-left: 0px;
  }
`;
